import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import {
  QueryScopeConfigId,
  type IQueryScopeRequests,
} from '@principle-theorem/principle-core/interfaces';
import {
  getDateRangeFilterFromQueryScopes,
  IReportBuilderQueryForm,
  QueryScopeBuilder,
  REPORT_BUILDER_DATA_SOURCES,
} from '@principle-theorem/reporting';
import { IReportBuilderDataSource } from '@principle-theorem/reporting/interfaces';
import { snapshot } from '@principle-theorem/shared';
import { first } from 'lodash';
import { combineLatest, type Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { QueryScopeFormComponent } from '../query-scope-form/query-scope-form.component';
import { QueryScopeForms } from '../query-scope-form/scope-form';
import { ReportBuilderStore } from '../report-builder.store';
import { ReportBuilderDateRangeForm } from './report-builder-date-range-form/report-builder-date-range';
import { ReportBuilderDateRangeFormComponent } from './report-builder-date-range-form/report-builder-date-range-form.component';

interface IReportBuilderQueryFormData
  extends Omit<IReportBuilderQueryForm, 'dataSource'> {
  dataSource: IReportBuilderDataSource;
}

class ReportBuilderQueryForm extends TypedFormGroup<IReportBuilderQueryFormData> {
  constructor(defaultDataSource?: IReportBuilderDataSource) {
    super({
      dataSource: new TypedFormControl<IReportBuilderDataSource>(
        defaultDataSource,
        Validators.required
      ),
      scopeRequests: new TypedFormControl<IQueryScopeRequests>({}),
    });
  }
}

@Component({
  selector: 'pr-report-builder-query-form',
  templateUrl: './report-builder-query-form.component.html',
  styleUrls: ['./report-builder-query-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButton,
    MatIcon,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltip,
    NgCommonModule,
    QueryScopeFormComponent,
    ReportBuilderDateRangeFormComponent,
  ],
})
export class ReportBuilderQueryFormComponent {
  dateRangeForm = new ReportBuilderDateRangeForm();
  dataSources = REPORT_BUILDER_DATA_SOURCES.filter(
    (dataSource) => !dataSource.isRetired
  );
  submitDisabled$: Observable<boolean>;
  queryForm = new ReportBuilderQueryForm(first(this.dataSources));
  queryScopeForms: QueryScopeForms;
  dataSource$: Observable<IReportBuilderDataSource>;

  constructor(
    private _store: ReportBuilderStore,
    private _organisation: OrganisationService
  ) {
    this.queryScopeForms = new QueryScopeForms(
      this._organisation.userBrands$,
      this._organisation.userPractices$,
      this._organisation.brand$
    );

    this.submitDisabled$ = combineLatest([
      this._store.isLoading$,
      this.queryForm.valueChanges.pipe(map(() => this.queryForm.valid)),
    ]).pipe(map(([isLoading, isValid]) => isLoading || !isValid));

    this.dataSource$ = validFormGroupChanges$(this.queryForm).pipe(
      map((form) => form.dataSource),
      startWith(this.queryForm.value.dataSource)
    );
  }

  async dataSourceHasBrandPracticeScopes(
    dataSource: IReportBuilderDataSource
  ): Promise<boolean> {
    const builder = new QueryScopeBuilder(dataSource.factTable);
    const hasBrandScope = builder.hasScope(this.queryScopeForms.brand.scopeId);
    const hasBrands = await snapshot(this.queryScopeForms.brand.isShown$);
    const hasPracticeScope = builder.hasScope(
      this.queryScopeForms.practice.scopeId
    );
    const hasPractices = await snapshot(this.queryScopeForms.practice.isShown$);

    return (hasBrandScope && hasBrands) || (hasPracticeScope && hasPractices);
  }

  async submit(): Promise<void> {
    const dateRange = this.dateRangeForm.value;
    const dateRangeFilter = getDateRangeFilterFromQueryScopes(
      dateRange.queryableTimestamp
    );

    const dataSource = await snapshot(this.dataSource$);
    const queryScopes = this.queryForm.value.scopeRequests;

    if (
      this._isScopeRequired(dataSource, QueryScopeConfigId.Brand) &&
      !queryScopes.brand
    ) {
      queryScopes.brand = await this.queryScopeForms.brand.getDefaultValue();
    }

    if (
      this._isScopeRequired(dataSource, QueryScopeConfigId.Practice) &&
      !queryScopes.practice
    ) {
      queryScopes.practice =
        await this.queryScopeForms.practice.getDefaultValue();
    }

    this._store.setDatasource(this.queryForm.value.dataSource.id);
    this._store.loadQuery({
      dataSource: this.queryForm.value.dataSource.id,
      scopeRequests: queryScopes,
      staticFilters: [dateRangeFilter],
      filterDate: dateRange.from,
      filterDateRange: dateRange,
      editMode: true,
      clearCurrentResults: true,
    });
  }

  private _isScopeRequired(
    dataSource: IReportBuilderDataSource,
    scopeId: QueryScopeConfigId
  ): boolean {
    return dataSource.factTable.scopes.some((scope) => scope.id === scopeId);
  }
}
